import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { CATEGORY_LOCATION } from "../utils/enums";
import { useCategories, sortOthersLast } from "../hooks/queries";

// Widths used to vary the skeleton chips so the loading row doesn't look
// like a single repeated stamp. Cycled through positionally.
const SKELETON_WIDTHS = [64, 88, 110, 72, 96, 80];

function CategoryChips() {
  const { data: cats = [], isLoading } = useCategories();
  const isReady = !isLoading;
  const [showAll, setShowAll] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const headerCats = cats.filter(
    (k: any) => k?.location === CATEGORY_LOCATION.HEADER,
  );

  // Sort any "Other"/"Others" category to the end while keeping the relative
  // order of everything else. Mirrors the ordering used in CategoryDropdown so
  // the modal matches the dropdowns on other pages.
  const sortedCats = sortOthersLast(cats);
  const isHome = location.pathname === "/";
  const isActiveCat = (id: string) => location.pathname === `/explore/${id}`;

  const choose = (to: string) => {
    setShowAll(false);
    navigate(to);
  };

  return (
    <div className="categoryChips" role="navigation" aria-label="Categories">
      <div className="categoryChips__header">
        <span className="categoryChips__kicker">Browse by category</span>
        <button
          type="button"
          className="categoryChips__viewAll"
          onClick={() => setShowAll(true)}
        >
          <span>View all</span>
          <i className="bi bi-arrow-right" aria-hidden="true" />
        </button>
      </div>

      <div className="categoryChips__scroll">
        {!isReady ? (
          SKELETON_WIDTHS.map((w, i) => (
            <span
              key={`skel-${i}`}
              className="categoryChips__chipSkeleton"
              style={{ width: w }}
              aria-hidden="true"
            />
          ))
        ) : (
          <>
            <NavLink
              to="/"
              end
              className={() =>
                `categoryChips__chip${
                  isHome ? " categoryChips__chip--active" : ""
                }`
              }
            >
              All
            </NavLink>
            {headerCats?.map((k: any) => (
              <NavLink
                to={`/explore/${k?._id}`}
                key={k?._id || k?.name}
                className={({ isActive }) =>
                  `categoryChips__chip${
                    isActive ? " categoryChips__chip--active" : ""
                  }`
                }
              >
                {k?.name}
              </NavLink>
            ))}
            <NavLink
              to="/explore/others"
              className={({ isActive }) =>
                `categoryChips__chip${
                  isActive ? " categoryChips__chip--active" : ""
                }`
              }
            >
              Others
            </NavLink>
          </>
        )}
      </div>

      <Modal
        show={showAll}
        onHide={() => setShowAll(false)}
        centered
        dialogClassName="categoryModal"
      >
        <Modal.Header closeButton>
          <div className="categoryModal__heading">
            <span className="categoryModal__kicker">Filter by</span>
            <Modal.Title>All Categories</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="categoryModal__grid">
            <button
              type="button"
              className={`categoryModal__option${
                isHome ? " categoryModal__option--active" : ""
              }`}
              onClick={() => choose("/")}
            >
              <span className="categoryModal__name">All</span>
            </button>
            {sortedCats?.map((k: any) => (
              <button
                key={k?._id || k?.name}
                type="button"
                className={`categoryModal__option${
                  isActiveCat(k?._id) ? " categoryModal__option--active" : ""
                }`}
                onClick={() => choose(`/explore/${k?._id}`)}
              >
                <span className="categoryModal__name">{k?.name}</span>
              </button>
            ))}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default CategoryChips;
